import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasCategoryContext, withCategory } from "@insite/client-framework/Components/CategoryContext";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Typography from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import * as React from "react";
import { css } from "styled-components";

const enum fields {
    image = "image",
    description = "description",
    slidTextAlignment = "slidTextAlignment",
    verticalTextAlignment = "verticalTextAlignment",
}

interface Props extends WidgetProps {
    fields: {
        [fields.image]: string;
        [fields.description]: string;
        [fields.slidTextAlignment]: string;
        [fields.verticalTextAlignment]: string;
    };
}

export interface CategoryDetailBannerStyles {
    container?: InjectableCss;
}

export interface CategoryDetailBannerStyles {
    containerStyles?: InjectableCss;
    innerWrapper?: InjectableCss;
    mainHeading?: InjectableCss;
    subHeading?: InjectableCss;
    mainHeadingWithTop?: InjectableCss;
}

export const bannerStyles: CategoryDetailBannerStyles = {
    innerWrapper: {
        css: css`
            width: 52.5%;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            width: 100%;
                            padding: 30px;
                        `,
                        css`
                            width: 100%;
                            padding: 30px;
                        `,
                        css`
                            width: 100%;
                            padding: 22px;
                        `,
                        css`
                            width: 52.5%;
                            padding: 0px;
                        `,
                        css`
                            width: 52.5%;
                            padding: 0px;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    mainHeading: {
        css: css`
            color: #ffffff;
            font-size: 18px;
            font-weight: bold;
            letter-spacing: 1.08px;
            line-height: 25px;
        `,
    },
    mainHeadingWithTop: {
        css: css`
            color: #ffffff;
            font-size: 18px;
            font-weight: bold;
            letter-spacing: 1.08px;
            line-height: 25px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        null,
                        css`
                            padding-top: 15px;
                        `,
                        css`
                            padding-top: 15px;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    subHeading: {
        css: css`
            .banner-subHeading {
                color: #ffffff;
                font-size: 44px;
                font-weight: 300;
                letter-spacing: 0;
                line-height: 53px;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            css`
                                font-size: 36px;
                                font-weight: 300;
                                letter-spacing: 0;
                                line-height: 43px;
                            `,
                            css`
                                font-size: 36px;
                                font-weight: 300;
                                letter-spacing: 0;
                                line-height: 43px;
                            `,
                            css`
                                font-size: 36px;
                                font-weight: 300;
                                letter-spacing: 0;
                                line-height: 43px;
                            `,
                            css`
                                font-size: 44px;
                                font-weight: 300;
                                letter-spacing: 0;
                                line-height: 53px;
                            `,
                            css`
                                font-size: 44px;
                                font-weight: 300;
                                letter-spacing: 0;
                                line-height: 53px;
                            `,
                        ],
                        "min",
                    )}
            }
        `,
    },
};

const styles = bannerStyles;

const CustomNewsBanner: React.FC<Props> = ({ fields }) => {
    const containerStyles = {
        css: css`
            background-image: url(${fields.image});
            min-height: 330px;
            background-size: cover;
            display: flex;
            justify-content: center;
            align-items: ${fields.verticalTextAlignment};
        `,
    };
    const outerStyles = {
        css: css`
            max-width: 1140px;
            margin: 0 auto;
            width: 100%;
            display: flex;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            max-width: 100%;
                        `,
                        css`
                            max-width: 100%;
                        `,
                        css`
                            justify-content: center;
                            align-items: center;
                            max-width: 720px;
                        `,
                        css`
                            justify-content: ${fields.slidTextAlignment};
                            align-items: ${fields.slidTextAlignment};
                            max-width: 960px;
                        `,
                        css`
                            justify-content: ${fields.slidTextAlignment};
                            align-items: ${fields.slidTextAlignment};
                            max-width: 1140px;
                        `,
                    ],
                    "min",
                )}
        `,
    };
    return (
        <StyledWrapper {...containerStyles}>
            <StyledWrapper {...outerStyles}>
                <StyledWrapper {...styles.innerWrapper}>
                    {fields.description && (
                        <Typography as="p" {...styles.subHeading}>
                            {parse(fields.description, parserOptions)}
                        </Typography>
                    )}
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const definition: WidgetDefinition = {
    group: "Categories",
    icon: "Banner",
    fieldDefinitions: [
        {
            name: fields.image,
            displayName: "Photo",
            editorTemplate: "ImagePickerField",
            defaultValue: "",
            fieldType: "General",
            sortOrder: 1,
        },
        {
            name: fields.description,
            displayName: "Description",
            editorTemplate: "RichTextField",
            extendedConfig: { height: 100 },
            defaultValue: "",
            fieldType: "Translatable",
            sortOrder: 6,
        },
        {
            name: fields.slidTextAlignment,
            editorTemplate: "DropDownField",
            defaultValue: "left",
            fieldType: "General",
            sortOrder: 0,
            options: [
                { displayName: "Left", value: "flex-start" },
                { displayName: "Center", value: "center" },
                { displayName: "Right", value: "flex-end" },
            ],
        },
        {
            name: fields.verticalTextAlignment,
            editorTemplate: "DropDownField",
            defaultValue: "center",
            fieldType: "General",
            sortOrder: 0,
            options: [
                { displayName: "Top", value: "flex-start" },
                { displayName: "Center", value: "center" },
            ],
        },
    ],
};
const widgetModule: WidgetModule = {
    component: CustomNewsBanner,
    definition,
};

export default widgetModule;
